import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';

import type { Publication } from '~/interfaces/publication';
import type { OrganizationSubscription, SubscriberPreference } from '~/interfaces/subscriber_preferences';
import type { Subscription } from '~/interfaces/subscriptions';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import getPublication from '~/services/api/publication/getPublication.server';
import getOrganizationSubscriptions from '~/services/api/subscription/getOrganizationSubscriptions.server';
import getPreferences from '~/services/api/subscription/getPreferences.server';
import getSubscriptionProfile from '~/services/api/subscription/getSubsciptionProfile.server';

import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';

import Container from './_components/Container';
import Downgrade from './_components/Downgrade';
import OrganizationSubscriptionPreferences from './_components/Preferences/OrganizationSubscriptionPreferences';
import Preferences from './_components/Preferences/Preferences';
import { AccountActionProvider } from './_context/account_action_context';
import { PreferencesProvider } from './_context/preferences_context';
import { usePreferencesContext } from './_context/usePreferencesContext';

import remixI18n from '~/i18next.server';
import { loaderSession } from '~/session';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const root: any = matches?.find((match) => match?.id === 'root');
  const publication = root?.data?.publication;
  const metaTitle = data?.metaTitle;

  if (!publication || !metaTitle) {
    return [{ robots: 'noindex' }];
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      robots: 'noindex',
      title: metaTitle,
      description: `Preferences - ${publication.name}`,
    },
  });
};

export const loader: LoaderFunction = async ({ request, params }) => {
  const { session, isLoggedIn } = await loaderSession(request);
  const url = new URL(request.url);

  const [publication, subscription, preferences, organizationSubscriptions] = await Promise.all([
    getPublication(request, session),
    getSubscriptionProfile(request, session, params?.subscriberId),
  ]).then(async ([publication, subscription]) => {
    return Promise.all([
      publication,
      subscription,
      getPreferences(request, session, subscription?.subscriber_id),
      getOrganizationSubscriptions(request, session, subscription?.subscriber_id),
    ]);
  });

  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  if (!subscription) {
    throw new Response(t('forms.toast.no_subscription'), { status: 404 });
  }

  return json({
    requestUrl: url.href,
    publication,
    subscription,
    preferences,
    organizationSubscriptions,
    subscriberId: params?.subscriberId,
    isLoggedIn,
    metaTitle: t('meta.titles.preferences'),
  });
};

export interface PreferencesLoaderData {
  requestUrl: string;
  publication: Publication;
  subscription: Subscription;
  preferences: SubscriberPreference[] | null;
  organizationSubscriptions: OrganizationSubscription[] | null;
  subscriberId: string;
  isLoggedIn: boolean;
  metaTitle: string;
}

const PreferencesWrapper = () => {
  const { subscription, publication, isLoggedIn, organizationSubscriptions } = usePreferencesContext();

  return (
    <Container
      isLoggedIn={isLoggedIn}
      subscriberId={subscription?.subscriber_id || ''}
      hasPreferencesEnabled={isLoggedIn && publication.has_subscriber_preferences}
    >
      {subscription && isLoggedIn ? (
        <>
          {organizationSubscriptions && organizationSubscriptions.length > 0 ? (
            <OrganizationSubscriptionPreferences />
          ) : null}
          <Preferences />
        </>
      ) : null}
      {Boolean(subscription) && <Downgrade />}
    </Container>
  );
};

const PreferencesPage = () => {
  const loaderData = useTypedLoaderData<PreferencesLoaderData>();

  return (
    <PreferencesProvider loaderData={loaderData}>
      <AccountActionProvider
        publication={loaderData.publication}
        isLoggedIn={loaderData.isLoggedIn}
        subscription={loaderData.subscription}
      >
        <PreferencesWrapper />
      </AccountActionProvider>
    </PreferencesProvider>
  );
};

export default PreferencesPage;
